import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Container,
  Divider,
  Grid,
  Link,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  OpenInNew as OpenInNewIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { applicationsApi, resumesApi } from '../../services/api';
import {
  APPLICATION_STATUSES,
  ApplicationDetail,
  ApplicationPatch,
  ApplicationStatus,
  Resume,
  statusMeta,
} from '../../types';
import { errorMessage } from '../../utils/errors';
import {
  formatDate,
  formatDateTime,
  fromDateTimeLocal,
  toDateInput,
  toDateTimeLocal,
} from '../../utils/format';
import { useToast } from '../../hooks/useToast';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import ApplicationAnswers from '../../components/applications/ApplicationAnswers';
import ApplicationMail from '../../components/mail/ApplicationMail';
import ApplicationTailored from '../../components/ai/ApplicationTailored';

const ApplicationDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { show, toast } = useToast();

  const [app, setApp] = useState<ApplicationDetail | null>(null);
  const [resumes, setResumes] = useState<Resume[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [descriptionOpen, setDescriptionOpen] = useState(false);
  const [interviewStage, setInterviewStage] = useState('');
  const [nextAction, setNextAction] = useState('');
  const [appliedAt, setAppliedAt] = useState('');
  const [notes, setNotes] = useState('');
  const [noteDraft, setNoteDraft] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const syncForm = (detail: ApplicationDetail) => {
    setInterviewStage(detail.interview_stage ?? '');
    setNextAction(toDateTimeLocal(detail.next_action_at));
    setAppliedAt(toDateInput(detail.applied_at));
    setNotes(detail.notes ?? '');
  };

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    const [detailResult, resumesResult] = await Promise.allSettled([
      applicationsApi.get(id),
      resumesApi.list(),
    ]);
    if (detailResult.status === 'fulfilled') {
      setApp(detailResult.value);
      syncForm(detailResult.value);
    } else {
      setError(errorMessage(detailResult.reason, 'Could not load this application.'));
    }
    if (resumesResult.status === 'fulfilled') setResumes(resumesResult.value);
    setLoading(false);
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const patch = async (data: ApplicationPatch, successMessage: string) => {
    if (!app) return;
    setSaving(true);
    try {
      const updated = await applicationsApi.patch(app.id, data);
      setApp(updated);
      syncForm(updated);
      show(successMessage);
    } catch (e) {
      show(errorMessage(e, 'Could not save.'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleAddNote = async () => {
    if (!app || !noteDraft.trim()) return;
    setAddingNote(true);
    try {
      const event = await applicationsApi.addEvent(app.id, {
        event_type: 'note',
        description: noteDraft.trim(),
      });
      setApp((prev) => (prev ? { ...prev, events: [event, ...prev.events] } : prev));
      setNoteDraft('');
      show('Note added.');
    } catch (e) {
      show(errorMessage(e, 'Could not add the note.'), 'error');
    } finally {
      setAddingNote(false);
    }
  };

  const handleDelete = async () => {
    if (!app) return;
    setDeleting(true);
    try {
      await applicationsApi.remove(app.id);
      navigate('/applications', { replace: true });
    } catch (e) {
      show(errorMessage(e, 'Could not delete the application.'), 'error');
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error || !app) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Button component={RouterLink} to="/applications" startIcon={<BackIcon />} sx={{ mb: 2 }}>
          Back to applications
        </Button>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={load}>
              Retry
            </Button>
          }
        >
          {error ?? 'Application not found.'}
        </Alert>
      </Container>
    );
  }

  const meta = statusMeta(app.status);
  const jobUrl = app.job.application_url || app.job.source_url;
  const sortedEvents = [...app.events].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Button component={RouterLink} to="/applications" startIcon={<BackIcon />} sx={{ mb: 2 }}>
        Back to applications
      </Button>

      {/* Header */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h4" component="h1" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                {app.job.title}
              </Typography>
              <Typography variant="h6" color="text.secondary">
                {app.job.company.name}
                {app.job.location ? ` | ${app.job.location}` : ''}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}>
                <Chip label={meta.label} size="small" sx={{ bgcolor: meta.color, color: 'white', fontWeight: 600 }} />
                {(app.ats_platform || app.job.source_platform) && (
                  <Chip label={app.ats_platform || app.job.source_platform} size="small" variant="outlined" />
                )}
                {app.applied_via_extension && <Chip label="Filled by extension" size="small" variant="outlined" color="primary" />}
                {app.job.salary_display && <Chip label={app.job.salary_display} size="small" variant="outlined" />}
              </Stack>
              {jobUrl && (
                <Link href={jobUrl} target="_blank" rel="noopener noreferrer" sx={{ display: 'inline-flex', alignItems: 'center', mt: 1 }}>
                  Open job posting <OpenInNewIcon fontSize="inherit" sx={{ ml: 0.5 }} />
                </Link>
              )}
            </Box>
            <Box sx={{ minWidth: { md: 260 } }}>
              <TextField
                select
                label="Status"
                size="small"
                fullWidth
                value={app.status}
                disabled={saving}
                onChange={(e) =>
                  patch({ status: e.target.value as ApplicationStatus }, 'Status updated.')
                }
              >
                {APPLICATION_STATUSES.map((s) => (
                  <MenuItem key={s.value} value={s.value}>
                    {s.label}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          </Stack>

          <Divider sx={{ my: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Applied on"
                type="date"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={appliedAt}
                disabled={saving}
                onChange={(e) => setAppliedAt(e.target.value)}
                onBlur={() => {
                  const next = appliedAt ? `${appliedAt}T00:00:00` : null;
                  if (toDateInput(app.applied_at) !== appliedAt) {
                    patch({ applied_at: next ? new Date(next).toISOString() : null }, 'Applied date saved.');
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Interview stage"
                size="small"
                fullWidth
                placeholder="e.g. Phone screen"
                value={interviewStage}
                disabled={saving}
                onChange={(e) => setInterviewStage(e.target.value)}
                onBlur={() => {
                  if (interviewStage !== (app.interview_stage ?? '')) {
                    patch({ interview_stage: interviewStage.trim() }, 'Interview stage saved.');
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Next action"
                type="datetime-local"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={nextAction}
                disabled={saving}
                onChange={(e) => setNextAction(e.target.value)}
                onBlur={() => {
                  if (nextAction !== toDateTimeLocal(app.next_action_at)) {
                    patch({ next_action_at: fromDateTimeLocal(nextAction) }, 'Next action saved.');
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                label="Resume used"
                size="small"
                fullWidth
                value={app.resume?.id ?? ''}
                disabled={saving}
                onChange={(e) =>
                  patch({ resume_id: e.target.value === '' ? null : e.target.value }, 'Resume updated.')
                }
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {resumes.map((r) => (
                  <MenuItem key={r.id} value={r.id}>
                    {r.label || r.filename}
                    {r.is_default ? ' (default)' : ''}
                  </MenuItem>
                ))}
                {app.resume && !resumes.some((r) => r.id === app.resume?.id) && (
                  <MenuItem value={app.resume.id}>{app.resume.label} (deleted)</MenuItem>
                )}
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        <Grid item xs={12} lg={8}>
          <Stack spacing={3}>
            {/* Job description */}
            <Card variant="outlined">
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="h6" fontWeight={600}>
                    Job description
                  </Typography>
                  {app.job.description && (
                    <Button
                      size="small"
                      onClick={() => setDescriptionOpen((o) => !o)}
                      endIcon={descriptionOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    >
                      {descriptionOpen ? 'Collapse' : 'Expand'}
                    </Button>
                  )}
                </Stack>
                {app.job.description ? (
                  <Collapse in={descriptionOpen} collapsedSize={96}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 1 }}>
                      {app.job.description}
                    </Typography>
                  </Collapse>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    No description was captured for this job.
                  </Typography>
                )}
              </CardContent>
            </Card>

            {/* Answers given */}
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Answers given
                </Typography>
                <ApplicationAnswers answers={app.answers} notify={show} />
              </CardContent>
            </Card>

            {/* Notes */}
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Notes
                </Typography>
                <TextField
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  multiline
                  minRows={4}
                  fullWidth
                  placeholder="Anything you want to remember about this application"
                  disabled={saving}
                />
                <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1 }}>
                  <Button
                    variant="contained"
                    size="small"
                    disabled={saving || notes === (app.notes ?? '')}
                    onClick={() => patch({ notes }, 'Notes saved.')}
                  >
                    Save notes
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Stack spacing={3}>
            {/* Timeline */}
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Timeline
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Add a note to the timeline"
                    value={noteDraft}
                    onChange={(e) => setNoteDraft(e.target.value)}
                    disabled={addingNote}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleAddNote();
                      }
                    }}
                  />
                  <Button variant="outlined" size="small" onClick={handleAddNote} disabled={addingNote || !noteDraft.trim()}>
                    {addingNote ? <CircularProgress size={16} /> : 'Add'}
                  </Button>
                </Stack>
                {sortedEvents.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No events yet.
                  </Typography>
                ) : (
                  <Stack spacing={1.5}>
                    {sortedEvents.map((ev) => (
                      <Box key={ev.id} sx={{ pl: 1.5, borderLeft: 2, borderColor: 'primary.light' }}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {formatDateTime(ev.created_at)} | {ev.event_type.replace(/_/g, ' ')}
                        </Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {ev.description}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                )}
              </CardContent>
            </Card>

            <ApplicationMail applicationId={app.id} onChanged={load} />
            <ApplicationTailored applicationId={app.id} notify={show} />

            {/* Meta */}
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Record
                </Typography>
                <Typography variant="body2">Created {formatDate(app.created_at)}</Typography>
                <Typography variant="body2">Updated {formatDate(app.updated_at)}</Typography>
                <Divider sx={{ my: 2 }} />
                <Button
                  color="error"
                  variant="outlined"
                  startIcon={<DeleteIcon />}
                  onClick={() => setConfirmDelete(true)}
                  fullWidth
                >
                  Delete application
                </Button>
              </CardContent>
            </Card>
          </Stack>
        </Grid>
      </Grid>

      <ConfirmDialog
        open={confirmDelete}
        title="Delete this application?"
        message={`Your record for ${app.job.title} at ${app.job.company.name}, including answers and timeline, will be permanently removed.`}
        confirmLabel="Delete"
        confirmColor="error"
        loading={deleting}
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(false)}
      />

      {toast}
    </Container>
  );
};

export default ApplicationDetailPage;
